import "../i18nFallback";
import { Sentry } from "../sentryInstrumentReact";
import React, { useState, useEffect, useRef } from 'react';
import Cookies from 'js-cookie';

import AddressForm from './AddressForm';
import { useCart } from './CartContext';
import { isValidEmail } from '../util/isValidEmail';
import ProcessingSpinner from "../components/ProcessingSpinner";

const validErrorStatusCodes = [400, 404];

/**
 * Retry a fetch with exponential backoff.
 * @param {Function} fetchFn - Function that returns a fetch Promise
 * @param {Object} options - Retry options
 * @param {number} options.maxRetries - Maximum number of retries (default: 2)
 * @param {number} options.baseDelayMs - Base delay in ms (default: 200)
 * @returns {Promise<Response>}
 */
async function fetchWithRetry(fetchFn, { maxRetries = 2, baseDelayMs = 200 } = {}) {
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fetchFn();
    } catch (error) {
      lastError = error;

      // Don't retry if it was intentionally aborted by the component
      if (error.name === 'AbortError') {
        throw error;
      }

      // If we've exhausted all retries, throw
      if (attempt >= maxRetries) {
        throw error;
      }

      // Exponential backoff: 200ms, 400ms, etc.
      const delay = baseDelayMs * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

function CheckoutAddressFormContainer() {
  const {
    setCartTax,
    shipping,
    setShipping,
    setCart,
    setCartTotal,
    checkoutFormData,
    setCheckoutFormData ,
    shippingSameAsBilling,
    shippingRequired,
    checkoutErrors,
    setRendering,
    setShippingSameAsBilling,
    setFetchingShippingCost,
    setFetchingTaxCost,
    clearCheckoutFieldError,
    discountCode,
    refreshCart,
    setDiscountNotice,
  } = useCart();

  const shippingMsg = window.gettext("Enter your full name, email, phone, and address to view available shipping options");
  const [shippingOptions, setShippingOptions] = useState([]);
  const [fetchingAccountData, setFetchingAccountData] = useState(true);
  const [fetchingShippingRates, setFetchingShippingRates] = useState(false);
  const [shippingError, setShippingError] = useState(shippingMsg);
  const csrftoken = Cookies.get('csrftoken');

  // Serialize shipping-rate POSTs and coalesce rapid clicks into the latest selection.
  const inFlightShippingRef = useRef(false);
  const pendingShippingRequestRef = useRef(null);
  // const showShippingOptions =
  //   (checkoutFormData.billing.addressComplete && shippingSameAsBilling && shippingError === '' && isValidEmail(checkoutFormData.billing.email)) ||
  //   (checkoutFormData.shipping.addressComplete && shippingError === '' && isValidEmail(checkoutFormData.billing.email));


  // fetch user data
  useEffect(() => {
    async function fetchUserData() {

      try {

        let data = {};
        let isBillingAddressComplete = false;
        let isShippingAddressComplete = false;

        // check session first and assign that data if already set
        const response1 = await fetch("/store/_api/customer/");
        if (!response1.ok) {
          throw new Error('Network response was not ok');
        }
        data = await response1.json();

        // if no data fallback to account info
        if (Object.entries(data).length === 0) {
          try {
            const response2 = await fetch("/accounts/_api/account/?cb=" + Date.now().toString(16));

            if (!response2.ok) {
			  if (!validErrorStatusCodes.concat(403).includes(response2.status)) {
                throw new Error("server error occurred: " + response2.status);
              }
            }

            data = await response2.json();

          } catch (error) {
            if (Sentry && Sentry.getClient()) {
                    Sentry.captureException(error);
            }
          }
        }
        // data = {
        //   first_name: "Bilbo",
        //   last_name: "Baggins",
        //   email: "bilbo@example.com",
        //   phone: "222-333-4333",
        //   billing_address: {
        //     street1: "310 E State St",
        //     city: "Scottville",
        //     region: "MI",
        //     postal_code: "49454",
        //     country: "USA"
        //   }
        // }

        isBillingAddressComplete = Boolean(
          data.billing_address &&
          (data.billing_address?.street1 || '') !== '' &&
          (data.billing_address?.city || '') !== '' &&
          (data.billing_address?.region || '') !== '' &&
          (data.billing_address?.postal_code || '') !== '' &&
          (data.billing_address?.country || '') !== ''
        );

        isShippingAddressComplete = Boolean(
          data.shipping_address &&
          (data.shipping_address?.street1 || '') !== '' &&
          (data.shipping_address?.city || '') !== '' &&
          (data.shipping_address?.region || '') !== '' &&
          (data.shipping_address?.postal_code || '') !== '' &&
          (data.shipping_address?.country || '') !== ''
        );

        setCheckoutFormData(prevState => ({
          ...prevState,
          billing: {
            ...prevState.billing,
            firstName: data.first_name || '',
            lastName: data.last_name || '',
            organization: data.organization_name || '',
            email: data.email || '',
            phone: data.phone || '',
            street1: data.billing_address?.street1 || '',
            street2: data.billing_address?.street2 || '',
            city: data.billing_address?.city || '',
            region: data.billing_address?.region || '',
            postalCode: data.billing_address?.postal_code || '',
            country: data.billing_address?.country,
            addressComplete: isBillingAddressComplete,
          },
          shipping: {
            ...prevState.shipping,
            firstName: data.first_name || '',
            lastName: data.last_name || '',
            organization: data.shipping_address?.organization_name || data.organization_name || '',
            street1: data.shipping_address?.street1 || '',
            street2: data.shipping_address?.street2 || '',
            city: data.shipping_address?.city || '',
            region: data.shipping_address?.region || '',
            postalCode: data.shipping_address?.postal_code || '',
            country: data.shipping_address?.country,
            addressComplete: isShippingAddressComplete,
          },
        }));

        // Only uncheck if shipping address is complete AND different from billing
        if (isShippingAddressComplete) {
          const billing = data.billing_address;
          const shipping = data.shipping_address;
          setShippingSameAsBilling(
				  billing.street1 == shipping.street1 &&
            billing.city == shipping.city &&
            billing.region == shipping.region &&
            billing.postalCode == shipping.postalCode &&
            billing.country == shipping.country
			  );
        }

      } catch (error) {
        Sentry.captureException(error);
      }
      setFetchingAccountData(false);

    }

    // only fetch user data on ROH English site
    if (document.querySelector('html').getAttribute('lang') === 'en') {
      fetchUserData();
    } else {
      // For non-English sites, just set fetching to false so the form loads
      setFetchingAccountData(false);
    }

  }, []);


  // Fetch Shipping Rates:
  // requires complete shipping address
  async function fetchShippingRates(controller) {
    setShippingOptions([]);
    setShipping({cost: 0, rateId: ""});
    setCartTax(0);
    setFetchingShippingRates(true);
    let shippingURL = '/store/_api/cart/shipping_rates/';

    if (shippingSameAsBilling && checkoutFormData.billing.addressComplete) {
      shippingURL += `?shipping_postal_code=${checkoutFormData.billing.postalCode}&shipping_region=${checkoutFormData.billing.region}&shipping_country=${checkoutFormData.billing.country}&shipping_street1=${checkoutFormData.billing.street1}`;
    } else if (checkoutFormData.shipping.addressComplete) {
      shippingURL += `?shipping_postal_code=${checkoutFormData.shipping.postalCode}&shipping_region=${checkoutFormData.shipping.region}&shipping_country=${checkoutFormData.shipping.country}&shipping_street1=${checkoutFormData.billing.street1}`;
    }

    if (
      (checkoutFormData.billing.addressComplete && shippingSameAsBilling ) ||
      (checkoutFormData.billing.addressComplete && checkoutFormData.shipping.addressComplete)
    ) {
      setShippingError('');

      try {
        const response = await fetch(shippingURL, {
          method: 'GET',
          headers: {"Content-Type": "application/json", "X-CSRFToken": csrftoken},
          signal: controller.signal,
        });

        if (!response.ok) {
          const errorObject = await response.json();
          console.log("errorObject:", errorObject);
          const firstKey = Object.keys(errorObject)[0];
          console.log(errorObject);
          setShippingError(errorObject[firstKey][0]);
        } else {
          setShippingError('');
          const data = await response.json();
          setShippingOptions(data);
          if (data.length) {
            handleShippingChange(data[0].id);
          }
        }

      } catch (error) {
        Sentry.captureException(error, {stage: "fetch shipping rates"});
        setShippingError(`There was an error fetching the shipping options: ${error}`);
      }
    } else {
      setShippingOptions([]);
    }
    setFetchingShippingRates(false);
  }


  // Fetch And Set Tax Rates:
  // Only used when shipping isn't required
  async function fetchAndSetTaxRate(controller) {
    setFetchingTaxCost(true);
    const formInfos = {
      email: checkoutFormData.billing.email,
      shipping_street1: checkoutFormData.billing.street1,
      shipping_street2: "",
      shipping_city: checkoutFormData.billing.city,
      shipping_country: checkoutFormData.billing.country,
      shipping_region: checkoutFormData.billing.region,
      shipping_postal_code: checkoutFormData.billing.postalCode,
      shipping_cost: 0,
    };

    try {
      const response = await fetchWithRetry(
        () => fetch("/store/_api/cart/set_tax/", {
          method: 'POST',
          body: JSON.stringify(formInfos),
          headers: {"Content-Type": "application/json", "X-CSRFToken": csrftoken},
          signal: controller.signal,
        }),
        { maxRetries: 2, baseDelayMs: 200 } // Retries at 200ms, 400ms (600ms total max delay)
      );

      if (!response.ok) {
        if (!validErrorStatusCodes.includes(response.status)) {
          throw new Error("server error occurred: " + response.status + " -- " + response.statusText);
        }
      }

      const data = await response.json();
      setCartTax(parseFloat(data.tax).toFixed(2))

    } catch (error) {
      // Don't report aborted requests (user navigated away or form changed)
      if (error.name !== 'AbortError') {
        Sentry.captureException(error, {
          tags: {stage: "fetch tax data"},
          extra: {
            status: error.status,
            statusText: error.statusText,
            responseText: error.responseText,
          },
        });
      }
    }
    setFetchingTaxCost(false);

  }


  // Re-check an applied discount once the shopper's email is known.
  // On failure the cart is already undiscounted; we just refresh local state.
  async function validateDiscountForEmail(controller) {
    if (discountCode === '') {
      return;
    }
    try {
      const response = await fetch("/store/_api/cart/discount/validate/", {
        method: "POST",
        body: JSON.stringify({ email: checkoutFormData.billing.email }),
        headers: {"Content-Type": "application/json", "X-CSRFToken": csrftoken},
        signal: controller.signal,
      });
      if (!response.ok) {
        if (response.status >= 500) {
          throw new Error("server error occurred: " + response.status + " -- " + response.statusText);
        }
        return;
      }
      const data = await response.json();
      await refreshCart(data);
      if (data.messages?.length) {
        setDiscountNotice(data.messages[0]);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        Sentry.captureException(error, {stage: "validate discount for email"});
      }
    }
  }


  // As form data is updated reveal next step
  // if shipping not required - calculate taxes
  // if shipping required - reveal shipping options
  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      if (isValidEmail(checkoutFormData.billing.email)) {
        await validateDiscountForEmail(controller);
        if (controller.signal.aborted) {
          return;
        }
        if (
          !shippingRequired &&
          checkoutFormData.billing.addressComplete
        ) {
          // fetch tax when shipping not required
          fetchAndSetTaxRate(controller);
        } else if (shippingRequired) {
          // fetch shipping rates when shipping required
          fetchShippingRates(controller);
        }
      }
    }, 3000);

    return () => {
      clearTimeout(timeoutId);
      controller.abort("time out");
    }
  }, [checkoutFormData, shippingSameAsBilling, discountCode]);


  // hide/show shipping address
  function handleCheckboxChange() {
    setShippingSameAsBilling(!shippingSameAsBilling);
    setCartTax(0);
    setShipping({
      cost: 0,
      id: "",
    });
  }


  async function fetchAndSetShippingAndTax(shippingInfos) {
    try {
      const response = await fetch('/store/_api/cart/shipping_rates/', {
        method: 'POST',
        body: shippingInfos,
        headers: {"X-CSRFToken": csrftoken},
      });

      if (!response.ok) {
        if (validErrorStatusCodes.includes(response.status)) {
			  const errorObject = await response.json();
			  console.log("errorObject:", errorObject);
          const firstKey = Object.keys(errorObject)[0];
          setShippingError(errorObject[firstKey][0]);
			  return { shippingCost: null, tax: null, total: null };
        } else {
			  throw new Error("server error occurred: " + response.status + " -- " + response.statusText);
			}
      }

      const data = await response.json();
      setCart(data);

      return {
        shippingCost: parseFloat(data.shipping_cost).toFixed(2),
        tax: parseFloat(data.tax).toFixed(2),
        total: parseFloat(data.total).toFixed(2),
      };

    } catch (error) {
      setShippingError(`There was an error calculating tax. ${error}`);
      Sentry.captureException(error, {stage: "setting shipping and tax"});
      return { shippingCost: null, tax: null, total: null };
    }
  }


  function buildShippingInfos(rateId) {
    const shippingInfos = new FormData();
    shippingInfos.append("shipping_rate_id", rateId);
    shippingInfos.append("email", checkoutFormData.billing.email);

    if (shippingSameAsBilling && checkoutFormData.billing.addressComplete) {
      shippingInfos.append("shipping_street1", checkoutFormData.billing.street1);
      shippingInfos.append("shipping_street2", checkoutFormData.billing.street2 || '');
      shippingInfos.append("shipping_city", checkoutFormData.billing.city);
      shippingInfos.append("shipping_postal_code", checkoutFormData.billing.postalCode);
      shippingInfos.append("shipping_region", checkoutFormData.billing.region);
      shippingInfos.append("shipping_country", checkoutFormData.billing.country);
    } else if (checkoutFormData.shipping.addressComplete) {
      shippingInfos.append("shipping_street1", checkoutFormData.shipping.street1);
      shippingInfos.append("shipping_street2", checkoutFormData.shipping.street2 || '');
      shippingInfos.append("shipping_city", checkoutFormData.shipping.city);
      shippingInfos.append("shipping_postal_code", checkoutFormData.shipping.postalCode);
      shippingInfos.append("shipping_region", checkoutFormData.shipping.region);
      shippingInfos.append("shipping_country", checkoutFormData.shipping.country);
    }
    return shippingInfos;
  }

  async function handleShippingChange(arg) {
    let rateId;
    if (typeof arg === "object" && arg.target.getAttribute('data-rate-id')) {
      rateId = arg.target.getAttribute('data-rate-id');
    } else if (typeof arg === "string") {
      rateId = arg;
    } else {
      console.error("handleShippingChange called without valid argument.");
      return;
    }

    clearCheckoutFieldError(["shipping_method"]);

    // NOTE: optimistic UI update so the radio feels instant
    setShipping({ rateId, cost: 0 });

    // NOTE: publish this as the latest desired selection, drop any earlier coalesced click
    pendingShippingRequestRef.current = {
      rateId,
      shippingInfos: buildShippingInfos(rateId),
    };

    // NOTE: a worker loop is already running; it will pick this up when its fetch resolves.
    if (inFlightShippingRef.current) {
      return;
    }

    inFlightShippingRef.current = true;
    setFetchingShippingCost(true);
    setFetchingTaxCost(true);

    try {
      while (pendingShippingRequestRef.current !== null) {
        const next = pendingShippingRequestRef.current;
        pendingShippingRequestRef.current = null;

        const { shippingCost, tax, total } = await fetchAndSetShippingAndTax(next.shippingInfos);

        // Skip committing if a newer click is queued, to avoid flashing a stale cost.
        if (
          ![total, shippingCost, tax].includes(null) &&
          pendingShippingRequestRef.current === null
        ) {
          setShipping({ rateId: next.rateId, cost: shippingCost });
          setCartTax(tax);
          setCartTotal(total);
        }
      }
    } finally {
      inFlightShippingRef.current = false;
      setFetchingShippingCost(false);
      setFetchingTaxCost(false);
    }
  }


  return (
    <div className="checkout-body">
      <fieldset>
        <h2>{window.gettext("Billing Information")}</h2>
        {fetchingAccountData ? <ProcessingSpinner /> : <AddressForm type="billing" />}
      </fieldset>
      {shippingRequired &&
        <fieldset>
          <h2>{window.gettext("Shipping Information")}</h2>
          <div id="div_id_shipping_same_as_billing" className="form-group check  ">
            <input
              type="checkbox"
              name="shipping_same_as_billing"
              id="id_shipping_same_as_billing"
              checked={shippingSameAsBilling}
              onChange={handleCheckboxChange}
              />
            <label className="mt-0" htmlFor="id_shipping_same_as_billing">{window.gettext("Shipping same as billing")}</label>
          </div>

          {!shippingSameAsBilling && <AddressForm type="shipping" />}

          <div className="form-group-row">

            {fetchingShippingRates ?
              (<ProcessingSpinner />)
            : shippingOptions.length > 0 ?
              (<div
                id="div_id_shipping_method"
                className={checkoutErrors.shipping_method?.length >= 1 ? "form-group error" : "form-group"}
                >
                <label htmlFor="id_shipping_method"><strong>{window.gettext("Choose Your Shipping Method")}</strong></label>
                <div className="radio-button-list">
                  {shippingOptions.map((option, index) => (
                    <div key={option.slug} className="form-check">
                      <input
                        type="radio"
                        name="shipping_method"
                        id={option.slug}
                        value={option.slug}
                        data-rate={option.rate}
                        data-rate-id={option.id}
                        onChange={handleShippingChange}
                        disabled={shippingOptions.length < 1 && !fetchShippingRates}
                        checked={option.id === shipping.rateId}
                        className="form-check-input"
                      />
                      <label className="form-check-label" htmlFor={option.slug}>{option.label} - <strong>${option.rate}</strong></label>
                    </div>
                  ))}
                </div>
                {checkoutErrors.shipping_method?.length >= 1 &&
                  <ul>
                    {checkoutErrors.shipping_method.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                }
              </div>)
            : (<></>)}
          </div>
          {shippingError !== '' &&
            (<div className="shipping-msg">
                <p className="error">
                    <span className="icon-error">⚠️</span>&nbsp;
                    {shippingError}
                </p>
            </div>)
          }
        </fieldset>
      }
    </div>
  );
}

export default CheckoutAddressFormContainer;
